"use client";

import { useEffect } from "react";
import { useT } from "@tillix/i18n/react";
import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import { Select } from "@tillix/ui-atoms/select";
import { Controller, useFormContext, useWatch } from "react-hook-form";

const MONTHS: readonly { value: string; label: string }[] = [
  { value: "01", label: "01 Jan" },
  { value: "02", label: "02 Feb" },
  { value: "03", label: "03 Mar" },
  { value: "04", label: "04 Apr" },
  { value: "05", label: "05 May" },
  { value: "06", label: "06 Jun" },
  { value: "07", label: "07 Jul" },
  { value: "08", label: "08 Aug" },
  { value: "09", label: "09 Sep" },
  { value: "10", label: "10 Oct" },
  { value: "11", label: "11 Nov" },
  { value: "12", label: "12 Dec" },
];

const YEARS: readonly string[] = (() => {
  const start = new Date().getFullYear();
  return Array.from({ length: 21 }, (_, i) => String(start + i));
})();

export interface ExpirationMonthYearPickerProps {
  monthName?: string;
  yearName?: string;
  monthId: string;
  yearId: string;
}

export function ExpirationMonthYearPicker({
  monthName = "cardExpirationMonth",
  yearName = "cardExpirationYear",
  monthId,
  yearId,
}: ExpirationMonthYearPickerProps) {
  const { control, setValue } = useFormContext();
  const { t } = useT();
  const { t: tInputs } = useT("inputs");
  const selectedYear = useWatch({ name: yearName }) as string | undefined;
  const selectedMonth = useWatch({ name: monthName }) as string | undefined;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const isCurrentYear = selectedYear === String(currentYear);
  const isPastMonth = !!selectedMonth && Number(selectedMonth) < currentMonth;

  useEffect(() => {
    if (isPastMonth && isCurrentYear) {
      setValue(yearName, "");
    }
  }, [isPastMonth, isCurrentYear, setValue, yearName]);

  return (
    <div className="flex gap-2">
      <Controller
        control={control}
        name={monthName}
        render={({ field, fieldState }) => (
          <FieldRoot className="min-w-0 flex-1" name={field.name}>
            <FieldLabel htmlFor={monthId}>
              {t("paymentForm.expMonth")}
            </FieldLabel>
            <FieldControl
              render={() => (
                <Select
                  onValueChange={(v) => field.onChange(v as string)}
                  value={field.value}
                >
                  <Select.Trigger className="h-10" id={monthId}>
                    <Select.Value placeholder="MM" />
                  </Select.Trigger>
                  <Select.Popup>
                    {MONTHS.map((m) => (
                      <Select.Item
                        disabled={
                          isCurrentYear && Number(m.value) < currentMonth
                        }
                        key={m.value}
                        value={m.value}
                      >
                        {m.label}
                      </Select.Item>
                    ))}
                  </Select.Popup>
                </Select>
              )}
            />
            <FieldError>
              {fieldState.error
                ? tInputs("validation.invalidExpiration")
                : undefined}
            </FieldError>
          </FieldRoot>
        )}
      />
      <Controller
        control={control}
        name={yearName}
        render={({ field, fieldState }) => (
          <FieldRoot className="min-w-0 flex-1" name={field.name}>
            <FieldLabel htmlFor={yearId}>{t("paymentForm.expYear")}</FieldLabel>
            <FieldControl
              render={() => (
                <Select
                  onValueChange={(v) => field.onChange(v as string)}
                  value={field.value}
                >
                  <Select.Trigger className="h-10" id={yearId}>
                    <Select.Value placeholder="YYYY" />
                  </Select.Trigger>
                  <Select.Popup>
                    {YEARS.filter(
                      (y) => !(isPastMonth && y === String(currentYear)),
                    ).map((y) => (
                      <Select.Item key={y} value={y}>
                        {y}
                      </Select.Item>
                    ))}
                  </Select.Popup>
                </Select>
              )}
            />
            <FieldError>
              {fieldState.error
                ? tInputs("validation.invalidExpiration")
                : undefined}
            </FieldError>
          </FieldRoot>
        )}
      />
    </div>
  );
}
